/** API client with JWT auth + transparent refresh. */

let accessToken: string | null = localStorage.getItem("gaole_access");
let refreshToken: string | null = localStorage.getItem("gaole_refresh");
let refreshPromise: Promise<boolean> | null = null;

export function setTokens(access: string | null, refresh?: string | null): void {
  accessToken = access;
  if (access) localStorage.setItem("gaole_access", access);
  else localStorage.removeItem("gaole_access");
  if (refresh !== undefined) {
    refreshToken = refresh;
    if (refresh) localStorage.setItem("gaole_refresh", refresh);
    else localStorage.removeItem("gaole_refresh");
  }
}

export function getAccessToken(): string | null {
  return accessToken;
}

/** Clear access token only (session lock). Refresh token stays for PIN re-login. */
export function clearAccessToken(): void {
  accessToken = null;
  localStorage.removeItem("gaole_access");
}

export class ApiError extends Error {
  status: number;
  code?: string;
  constructor(status: number, message: string, code?: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

async function tryRefresh(): Promise<boolean> {
  if (!refreshToken) return false;
  if (!refreshPromise) {
    refreshPromise = (async () => {
      try {
        const res = await fetch("/api/v1/auth/refresh", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refreshToken }),
        });
        if (!res.ok) return false;
        const data = await res.json();
        setTokens(data.accessToken);
        return true;
      } catch {
        return false;
      } finally {
        refreshPromise = null;
      }
    })();
  }
  return refreshPromise;
}

/** Restore a valid access token from the stored refresh token (e.g. after page reload). */
export async function refreshAccessToken(): Promise<boolean> {
  return tryRefresh();
}

export async function api<T = unknown>(
  path: string,
  options: { method?: string; body?: unknown; formData?: FormData } = {},
): Promise<T> {
  const doFetch = () =>
    fetch(`/api/v1${path}`, {
      method: options.method ?? (options.body || options.formData ? "POST" : "GET"),
      headers: {
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        ...(options.body ? { "Content-Type": "application/json" } : {}),
      },
      body: options.formData ?? (options.body ? JSON.stringify(options.body) : undefined),
    });

  let res = await doFetch();
  if (res.status === 401 && refreshToken && (await tryRefresh())) {
    res = await doFetch();
  }
  if (res.status === 403) {
    try {
      const data = (await res.clone().json()) as { code?: string };
      if (data.code === "phone_verification_required") {
        const path = window.location.pathname;
        if (!path.startsWith("/account/verify-phone")) {
          const from = path !== "/login" && path !== "/register" ? path : undefined;
          if (from) sessionStorage.setItem("gaole_verify_from", from);
          window.location.assign("/account/verify-phone");
        }
      }
    } catch {
      /* ignore parse errors */
    }
  }
  if (!res.ok) {
    let message = "Something went wrong";
    let code: string | undefined;
    if (res.status === 413) {
      message = "File is too large — each receipt must be 12 MB or smaller";
    } else {
      try {
        const data = (await res.json()) as { error?: string; message?: string; code?: string };
        message = data.error ?? data.message ?? message;
        code = data.code;
      } catch {
        if (res.status >= 500) message = "Server error — please try again shortly";
      }
    }
    throw new ApiError(res.status, message, code);
  }
  const contentType = res.headers.get("content-type") ?? "";
  if (contentType.includes("application/json")) return res.json();
  return undefined as T;
}

export async function apiUpload<T = unknown>(path: string, file: File, fieldName = "file"): Promise<T> {
  const formData = new FormData();
  formData.append(fieldName, file);
  return api<T>(path, { method: "POST", formData });
}

export async function apiDownload(path: string, filename: string): Promise<void> {
  const blob = await fetchBlob(path);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/** Open an authenticated file (e.g. PDF receipt) in a new browser tab. */
export async function apiOpen(path: string): Promise<void> {
  const tab = window.open("", "_blank");
  try {
    const blob = await fetchBlob(path);
    const url = URL.createObjectURL(blob);
    if (tab) tab.location.href = url;
    else window.location.href = url;
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (err) {
    tab?.close();
    throw err;
  }
}

async function fetchBlob(path: string): Promise<Blob> {
  const doFetch = () =>
    fetch(`/api/v1${path}`, {
      headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
    });

  let res = await doFetch();
  if (res.status === 401 && refreshToken && (await tryRefresh())) {
    res = await doFetch();
  }
  if (!res.ok) {
    let message = "Download failed";
    try {
      const data = (await res.json()) as { error?: string; message?: string };
      message = data.error ?? data.message ?? message;
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(res.status, message);
  }
  return res.blob();
}
